
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { exportFlowchartWithErrorHandling } from "@/utils/exports/flowchartExport";
import { Node, Edge } from "@xyflow/react";
import { FileText, Image, FileType, Download, X } from "lucide-react";

interface FlowchartExportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodes: Node[];
  edges: Edge[];
  whiteboardName?: string;
}

const FlowchartExport: React.FC<FlowchartExportProps> = ({
  open,
  onOpenChange,
  nodes,
  edges,
  whiteboardName = "My Flowchart",
}) => {
  const [exportName, setExportName] = useState(whiteboardName);
  const [activeFormat, setActiveFormat] = useState<'pdf' | 'jpeg' | 'word'>('pdf');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      await exportFlowchartWithErrorHandling(activeFormat, nodes, edges, exportName);
      onOpenChange(false);
    } finally {
      setIsExporting(false);
    }
  };

  const formatOptions = [
    {
      key: 'pdf' as const,
      icon: <FileText size={16} />,
      label: 'PDF',
      description: 'High-quality document with flowchart image and node details'
    },
    {
      key: 'jpeg' as const,
      icon: <Image size={16} />,
      label: 'JPEG',
      description: 'High-resolution image of your flowchart'
    },
    {
      key: 'word' as const,
      icon: <FileType size={16} />,
      label: 'Word',
      description: 'HTML document that opens in Microsoft Word with embedded image'
    }
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => onOpenChange(false)}></div>
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Export Flowchart</h2>
              <p className="text-sm text-gray-600 mt-1">
                Save your flowchart to your computer in your preferred format.
              </p>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="export-name">File name</Label>
              <Input 
                id="export-name" 
                value={exportName} 
                onChange={(e) => setExportName(e.target.value)}
                placeholder="Enter a name for your export"
              />
            </div>

            <div className="space-y-2">
              <Label>Export format</Label>
              <div className="grid gap-2">
                {formatOptions.map((format) => (
                  <button
                    key={format.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                      activeFormat === format.key 
                        ? "border-blue-500 bg-blue-50" 
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() => setActiveFormat(format.key)}
                  >
                    <div className="mt-0.5">{format.icon}</div>
                    <div className="flex-1">
                      <div className="font-medium">{format.label}</div>
                      <div className="text-sm text-gray-600">{format.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-start gap-2 mt-6">
            <Button
              type="button"
              onClick={handleExport}
              disabled={isExporting || !exportName.trim()}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => onOpenChange(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowchartExport;
